/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
import { systemRoles } from '@/atoms';
import { axios } from '@/axios.config.ts';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { UserRole } from '@/types';
import { IUser, Role } from '@/types/user.type';
import { getUseRoute } from '@/utils';
import Cookies from 'js-cookie';
import { Logo } from 'moh-ui';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil-next';

interface IAuthContext {
   user: IUser | null;
   initialLoading: boolean;
   logout: () => Promise<void>;
   getRoles: () => Promise<void>;
   loading: boolean;
   profile: {
      user: IUser;
      employee: any;
      roles: Role[];
   } | null;
   isAdmin: boolean;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

export const nonProtectedRoutes = [
   '/auth/login',
   '/auth/forgot-password',
   '/auth/verify-email',
   '/auth/reset-password',
   '/auth/accept-invitation',
   '/on-boarding/fill-profile',
   '/auth/reset-password',
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
   const navigate = useNavigate();
   const location = useLocation();
   const [initialLoading, setInitialLoading] = useState(true);
   // this state is used to show a loading indicator when the user is logging in or out
   const [loading, setLoading] = useState(false);
   const [user, setUser] = useState<IUser | null>(null);
   const [profile, setProfile] = useState<any>(null);
   const [roles, setRoles] = useRecoilState(systemRoles);
   const [isAdmin, setIsAdmin] = useState(false);

   const token = Cookies.get('token');
   const isWhiteListed = nonProtectedRoutes.some((route) => location.pathname.startsWith(route));

   const getProfile = async () => {
      setLoading(true);
      try {
         const res = await axios.get('/auth/profile');
         setUser(res.data.data.user);
         setProfile(res.data.data);
         setIsAdmin(res.data.data.roles.some((role: Role) => role.roleName === UserRole.ADMIN));
      } catch (error) {
         console.log(error);
         setInitialLoading(false);
      }
      setLoading(false);
   };

   const getRoles = async () => {
      try {
         const res = await axios.get('/roles/all');
         // console.log(res.data);
         setRoles(res.data.data as Role[]);
      } catch (error) {
         console.log(error);
      }
   };

   useEffect(() => {
      if (!roles || !roles.length) getRoles();
      if (token) getProfile();
   }, []);

   useEffect(() => {
      if (token && roles.length && profile && user) setInitialLoading(false);
      else if (!token) setInitialLoading(false);
   }, [roles, user, profile]);

   const logout = async () => {
      setLoading(true);
      setUser(null);
      Cookies.remove('token');
      setLoading(false);
      toast.success('Logout successful');
      navigate('/auth/login');
      window.location.reload();
   };

   if (initialLoading) {
      return (
         <div className="flex items-center justify-center h-screen ">
            <div className="animate-pulse flex flex-col  items-center">
               <Logo subtitle="Insurance Services Portal" />
               <div className="flex items-center mt-4 gap-4">
                  <LoadingSpinner />
                  <h1 className="opacity-60"> HEre Loading...</h1>
               </div>
            </div>
         </div>
      );
   }
   if (isWhiteListed && user && token) {
      const nextUrl = getUseRoute(profile.roles[0].roleName);
      return <Navigate to={nextUrl} />;
   }

   if (!user && !initialLoading && !isWhiteListed) {
      return <Navigate to="/auth/login" />;
   }

   return (
      <AuthContext.Provider value={{ user, getRoles, initialLoading, logout, loading, profile, isAdmin }}>
         {children}
      </AuthContext.Provider>
   );
}

export default function useAuth() {
   const context = useContext(AuthContext);
   if (context === undefined) {
      throw new Error('useAuth must be used within an AuthProvider');
   }
   return context;
}
